import type { ProductCardDTO } from "../api/types.js";
import { formatAvailabilityLabel } from "./format.js";

const RAW_AVAILABILITY_KEYS = new Set([
  "in_stock",
  "low_stock",
  "preorder",
  "out_of_stock",
  "unknown",
]);

/** Customer-visible stock text — never raw enum values. */
export function customerStockLabel(
  product: Pick<
    ProductCardDTO,
    "stock_display" | "availability_label" | "availability" | "stock_status" | "stock_quantity"
  >,
): string {
  const display = product.stock_display?.trim();
  if (display && !RAW_AVAILABILITY_KEYS.has(display)) {
    return display;
  }
  const rawLabel = product.availability_label?.trim();
  if (rawLabel && !RAW_AVAILABILITY_KEYS.has(rawLabel)) {
    return rawLabel;
  }
  return formatAvailabilityLabel(product.availability, product.stock_status);
}

export function isInstantPurchasable(
  product: Pick<
    ProductCardDTO,
    "availability" | "purchasable" | "price_resolved" | "stock_status" | "payable_price_uah"
  >,
): boolean {
  const availability = (product.availability ?? "unknown") as
    | "in_stock"
    | "low_stock"
    | "preorder"
    | "out_of_stock"
    | "unknown";
  if (availability === "out_of_stock" || availability === "preorder" || availability === "unknown") {
    return false;
  }
  const stock = String(product.stock_status ?? "").toLowerCase();
  if (stock === "out_of_stock") {
    return false;
  }
  if (product.purchasable === false) {
    return false;
  }
  if (product.price_resolved === false) {
    return false;
  }
  if (
    product.payable_price_uah != null &&
    (!Number.isFinite(Number(product.payable_price_uah)) || Number(product.payable_price_uah) <= 0)
  ) {
    return false;
  }
  return true;
}

export function customerAvailabilityLabel(
  product: Pick<ProductCardDTO, "availability" | "availability_label" | "stock_status">,
): string {
  return customerStockLabel(product);
}

export function isHomePrimaryProduct(product: Pick<ProductCardDTO, "availability">): boolean {
  const availability = product.availability;
  return availability === "in_stock" || availability === "low_stock";
}

export function isHomePreorderProduct(product: Pick<ProductCardDTO, "availability">): boolean {
  return product.availability === "preorder";
}

export function unavailablePurchaseHint(
  product: Pick<ProductCardDTO, "availability" | "price_resolved" | "purchasable">,
): string | null {
  if (product.price_resolved === false) {
    return "Ціна не підтверджена";
  }
  if (product.purchasable === false && product.availability === "in_stock") {
    return "Зараз недоступно для кошика";
  }
  switch (product.availability) {
    case "preorder":
      return "Під замовлення — уточніть у менеджера";
    case "unknown":
      return "Наявність не підтверджена";
    case "out_of_stock":
      return "Немає в наявності";
    default:
      return null;
  }
}

export function shouldRenderPurchaseButton(
  product: Pick<
    ProductCardDTO,
    "availability" | "purchasable" | "price_resolved" | "stock_status" | "payable_price_uah"
  >,
  onAddToCart?: boolean,
): boolean {
  return Boolean(onAddToCart) && isInstantPurchasable(product);
}
